<template>
  <div class="category-field-wrapper">
    <label for="category" class="block text-sm font-medium text-gray-700 mb-1">{{label}}</label>

    <div
        id="category"
        ref="trigger"
        @click="openDialog"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 cursor-pointer flex items-center justify-between"
        :class="{'border-red-300': errors.category}"
    >
      <span v-if="selectedCategories.length === 0" class="text-gray-400">انتخاب کنید</span>
      <div v-else class="flex flex-wrap gap-1 rtl:space-x-reverse">
        <span
            v-for="cat in selectedCategories"
            :key="cat.id"
            class="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-lg inline-flex items-center w-auto"
        >
          {{ getCategoryName(cat) }}
          <button @click.stop="removeCategory(cat)" class="mr-1 text-blue-500 hover:text-blue-700 text-sm">&times;</button>
        </span>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 ml-2" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </div>

    <p v-if="errors.category" class="mt-1 text-sm text-red-600 text-right">{{ errors.category }}</p>

    <transition name="fade-scale">
      <div
          ref="overlay"
          v-if="dialogOpen"
          class="modal-overlay"
          @click="closeDialog"
          role="dialog"
          aria-modal="true"
      >
        <div
            ref="panel"
            class="dropdown-panel"
            @click.stop
            @keydown.esc.stop.prevent="closeDialog"
        >
          <div class="bg-white rounded-lg overflow-hidden shadow-xl w-full border border-gray-200">
            <div class="bg-gray-900 px-4 pt-5 pb-4 sm:p-6 border-b border-gray-800 sticky top-0 z-10">

              <div class="flex items-start justify-between mb-4">
                <h3 class="text-lg leading-6 font-medium text-white text-center sm:text-right">
                  {{ createOpen ? 'ایجاد' : 'انتخاب' }}
                  {{label}}
                </h3>

                <button
                    class="text-xs px-2 py-1 rounded-md bg-gradient-to-r from-green-600 to-cyan-600 text-white font-medium shadow hover:opacity-90 transition"
                    @click.stop="toggleCreateForm"
                >
                  {{ !createOpen ? 'ایجاد' : 'انصراف ' }}

                  {{label}}
                  جدید

                </button>
              </div>

              <div class="mb-0" v-if="!createOpen">
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="جستجو..."
                    class="text-right w-full rtl px-3 py-2 border bg-gray-700 border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-50"
                    dir="rtl"
                >
              </div>
            </div>


            <div
                v-if="createOpen"
                class="px-4 py-3 bg-blue-50 border-b border-blue-100 text-right"
                dir="rtl"
            >
              <div class="mb-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">نام </label>
                <input
                    type="text"
                    v-model="createForm.name"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                    :class="{'border-red-400': createErrors.name}"
                    placeholder="نام را وارد کنید"
                >
                <p v-if="createErrors.name" class="text-xs text-red-600 mt-1">{{ createErrors.name }}</p>
              </div>

              <div class="mb-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">زیرمجموعهٔ (والد)</label>

                <div
                    ref="parentTrigger"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-between cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                    :class="{'border-red-400': createErrors.parent}"
                    @click="toggleParentDropdown"
                >
                  <span class="text-right truncate">
                    <template v-if="createForm.parent">
                      {{ parentSelectedLabel }}
                    </template>
                    <template v-else>
                      <span class="text-gray-400">(بدون والد / ریشه)</span>
                    </template>
                  </span>
                  <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-gray-500 ml-2 flex-shrink-0"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.19l3.71-3.96a.75.75 0 111.08 1.04l-4.24 4.52a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                  </svg>
                </div>

                <p v-if="createErrors.parent" class="text-xs text-red-600 mt-1">{{ createErrors.parent }}</p>
              </div>
              <div class="mb-4">
                <label class="block text-xs font-medium text-gray-700 mb-1">تصویر (اختیاری)</label>

                <input
                    type="file"
                    accept="image/*"
                    @change="onImageChange"
                    class="block w-full text-sm text-gray-600"
                >

                <div
                    v-if="imagePreview"
                    class="mt-2 inline-flex items-center border border-gray-300 rounded-lg p-2 bg-white"
                >
                  <button
                      @click="removeSelectedImage"
                      class="bg-rose-600 text-white rounded-full px-2 py-1 ml-3"
                      title="حذف">✕
                  </button>

                  <img
                      :src="imagePreview"
                      alt="preview"
                      class="h-12 w-12 object-cover rounded border border-gray-200"
                  >

                </div>

                <p v-if="createErrors.image" class="text-xs text-red-600 mt-1">{{ createErrors.image }}</p>
              </div>



              <div class="flex items-center justify-end gap-2">
                <button
                    class="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                    @click="cancelCreateForm"
                    :disabled="loadingCreate"
                >
                  انصراف
                </button>

                <button
                    class="max-w-md items-center px-4 py-2 text-sm rounded-md font-medium text-white bg-gradient-to-r from-green-600 to-cyan-600 shadow hover:opacity-90 flex text-center disabled:opacity-50"
                    @click="submitCreate"
                    :disabled="loadingCreate"
                >
                  <svg
                      v-if="loadingCreate"
                      class="animate-spin h-4 w-4 ml-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                  >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v4l3.5-3.5L12 20v4a8 8 0 01-8-8z"
                    ></path>
                  </svg>
                  <span>ثبت {{label}} جدید</span>
                </button>
              </div>

              <p v-if="createErrors._error" class="text-xs text-red-600 mt-2 text-left ltr:text-left rtl:text-right">
                {{ createErrors._error }}
              </p>
              <div
                  v-if="parentDropdownOpen"
                  class="fixed z-[100002] bg-white border border-gray-200 rounded-lg shadow-xl text-sm"
                  :style="{
                    top: parentDropdownPos.top + 'px',
                    left: parentDropdownPos.left + 'px',
                    width: parentDropdownPos.width + 'px'
                  }"
              >
                <div class="p-2 border-b border-gray-100">
                  <input
                      type="text"
                      v-model="parentSearch"
                      placeholder="جستجو در دسته‌ها..."
                      class="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                </div>

                <div class="max-h-48 overflow-y-auto">
                  <div
                      class="px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between"
                      :class="{'bg-blue-100': createForm.parent === null}"
                      @click.stop="selectParent(null)"
                  >
                    <span class="text-gray-700 text-xs">
                      — بدون والد (دسته ریشه) —
                    </span>
                    <span
                        v-if="createForm.parent === null"
                        class="text-blue-600 text-[10px] font-medium"
                    >انتخاب شد</span>
                  </div>

                  <div
                      v-for="cat in filteredParentOptions"
                      :key="cat.id"
                      class="px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between"
                      :class="{'bg-blue-100': createForm.parent === cat.id}"
                      @click.stop="selectParent(cat.id)"
                  >
                    <span
                        class="text-gray-700 text-xs truncate"
                        :style="{ paddingRight: (cat.depth * 12) + 'px' }"
                    >
                        {{ cat.label }}
                      </span>

                    <span
                        v-if="createForm.parent === cat.id"
                        class="text-blue-600 text-[10px] font-medium"
                    >انتخاب شد</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!createOpen" class="tree-container overflow-y-auto px-4 py-3" dir="rtl">
              <category-item
                  :search-query="searchQuery"
                  v-for="category in filteredCategories"
                  :key="category.id"
                  :category="category"
                  :selected-categories="selectedCategories"
                  @toggle="toggleCategory"
                  @toggle-select="toggleCategorySelect"
              />
            </div>

            <div v-if="!createOpen" class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 sticky bottom-0 z-10">
              <button
                  type="button"
                  class="px-4 py-2 border border-transparent rounded-md white--text text-sm font-medium bg-gradient-to-r from-blue-500 to-blue-600 items-center"
                  @click="confirmAndClose"
              >
                <span class="text-gray-50">
                  تایید
                </span>

              </button>
              <button
                  type="button"
                  class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 mr-2"
                  @click="closeDialog"
              >انصراف</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CategoryItem from "@/modules/product_admin/CategoryItem";

export default {
  components: { CategoryItem },
  props: {
    categories: { type: Array, required: true },
    value: { type: Array, default: () => [] },
    label: { type: String, default: 'دسته بندی' },
    apiUrl: { type: String, default: 'product/categorization' },
    errors: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      dialogOpen: false,
      searchQuery: '',
      selectedCategories: [...(this.value || [])],
      processedCategories: [],
      _prevOverflow: '',
      _prevPaddingRight: '',
      _prevBodyStyles: null,
      _lockY: 0,
      _movedToBody: false,
      parentDropdownOpen: false,
      parentDropdownPos: {
        top: 0,
        left: 0,
        width: 0,
      },
      createOpen: false,
      createForm: {
        name: '',
        parent: null,
        image: null,
      },
      parentSearch: '',
      imagePreview: null,
      createErrors: {},
      loadingCreate: false,

    }
  },
  created() {
    this.processedCategories = this.processCategories(this.categories);
  },
  methods: {
    selectParent(id) {
      this.createForm.parent = id;
      this.parentDropdownOpen = false;
    },

    toggleParentDropdown() {
      if (this.parentDropdownOpen) {
        this.parentDropdownOpen = false;
        return;
      }

      const trigger = this.$refs.parentTrigger;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        this.parentDropdownPos = {
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        };
      }

      this.parentDropdownOpen = true;
    },

    toggleCreateForm() {
      this.createOpen = !this.createOpen;
      if (this.createOpen) {
        this.createErrors = {};
      }
      this.parentDropdownOpen = false;
    },
    cancelCreateForm() {
      this.createOpen = false;
      this.createErrors = {};
      this.createForm = {
        name: '',
        parent: null,
        image: null
      };
      this.parentSearch = '';
      this.imagePreview = null;
      this.parentDropdownOpen = false;
    },
    onImageChange(e) {
      const file = e.target.files && e.target.files[0];
      this.createForm.image = file || null;

      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.imagePreview = ev.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },
    removeSelectedImage() {
      this.createForm.image = null;
      this.imagePreview = null;

    },

    normalizeErrors(errData) {
      const out = {};
      for (const key in errData) {
        if (Array.isArray(errData[key])) {
          out[key] = errData[key].join(' ، ');
        } else if (typeof errData[key] === 'string') {
          out[key] = errData[key];
        } else {
          out[key] = 'نامعتبر';
        }
      }
      return out;
    },
    insertNewCategoryIntoTree(newCat) {
      const nodeTemplate = {
        ...newCat,
        level: 0,
        parent: null,
        expanded: false,
        _visible: true,
        children: []
      };

      const parentId = (newCat.parent && newCat.parent.id) ? newCat.parent.id : newCat.parent;

      if (!parentId) {
        this.processedCategories.push(nodeTemplate);
        return;
      }

      const addToParent = (nodes, pid) => {
        for (const n of nodes) {
          if (n.id === pid) {
            if (!n.children) n.children = [];
            n.children.push({
              ...nodeTemplate,
              level: (n.level || 0) + 1,
              parent: n
            });
            n.expanded = true;
            return true;
          }
          if (n.children && n.children.length) {
            const ok = addToParent(n.children, pid);
            if (ok) return true;
          }
        }
        return false;
      };

      addToParent(this.processedCategories, parentId);
    },
    submitCreate() {
      if (this.loadingCreate) return;
      this.loadingCreate = true;
      this.createErrors = {};

      const fd = new FormData();
      fd.append('name', this.createForm.name || '');
      if (this.createForm.parent) {
        fd.append('parent', this.createForm.parent);
      }
      if (this.createForm.image) {
        fd.append('image', this.createForm.image);
      }

      this.request({
        url: this.endpoint(this.apiUrl),
        method: 'post',
        data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },

        success: (data) => {
          this.insertNewCategoryIntoTree(data);

          this.selectedCategories = [
            ...this.selectedCategories,
            data
          ];

          this.cancelCreateForm();

          if (typeof this.showNotificationMessage === 'function') {
            this.showNotificationMessage('دسته‌بندی با موفقیت ساخته شد');
          }

          this.loadingCreate = false;
        },

        error: (err) => {
          let payload = err && err.response && err.response.data ? err.response.data : err;
          if (!payload || typeof payload !== 'object') {
            payload = { _error: 'خطا در ثبت دسته جدید' }
          }
          this.createErrors = this.normalizeErrors(payload);
          this.loadingCreate = false;
        }
      });
    },

    processCategories(categories, level = 0, parent = null) {
      return categories.map(cat => ({
        ...cat,
        level,
        parent,
        expanded: false,
        _visible: true,
        children: cat.children ? this.processCategories(cat.children, level + 1, cat) : []
      }));
    },
    collapseAll() {
      const walk = (nodes) => {
        for (const n of nodes) {
          n.expanded = false;
          n._visible = true;
          if (n.children && n.children.length) walk(n.children);
        }
      };
      walk(this.processedCategories);
    },
    pruneTree(nodes, q) {
      const query = (q || '').toLowerCase().trim();
      if (!Array.isArray(nodes) || !nodes.length) return [];

      const out = [];
      for (const n of nodes) {
        const name = String(n.name || '').toLowerCase();
        const selfMatch = query ? name.includes(query) : true;

        const children = Array.isArray(n.children) ? n.children : [];
        const prunedChildren = query ? this.pruneTree(children, query) : children;

        if (selfMatch || prunedChildren.length) {
          out.push({
            ...n,
            expanded: query ? true : !!n.expanded,
            children: prunedChildren
          });
        }
      }
      return out;
    },
    searchAndExpand(node, q) {
      const selfMatch = (node.name || '').toLowerCase().includes(q);
      let childMatch = false;
      for (const child of (node.children || [])) {
        const childHas = this.searchAndExpand(child, q);
        childMatch = childMatch || childHas;
      }
      node._visible = selfMatch || childMatch;
      node.expanded = childMatch;
      return node._visible;
    },
    applySearch(q) {
      const query = (q || '').toLowerCase().trim();
      this.collapseAll();
      if (!query) return;
      for (const root of this.processedCategories) {
        this.searchAndExpand(root, query);
      }
    },

    openDialog() {
      this.dialogOpen = true;
      this.searchQuery = '';

      this.$nextTick(() => {
        const el = this.$refs.overlay;
        if (el && !this._movedToBody) {
          document.body.appendChild(el);
          this._movedToBody = true;
        }

        this.lockScroll();
      });
    },

    confirmAndClose() {
      this.$emit('input', this.selectedCategories);
      this.closeDialog();
    },

    closeDialog() {
      this.dialogOpen = false;
      this.unlockScroll();
    },

    // --- قفل اسکرول استاندارد (position: fixed + جبران اسکرول‌بار)
    lockScroll() {
      const body = document.body;
      const docEl = document.documentElement;

      // ذخیره استایل قبلی برای بازگردانی
      if (!this._prevBodyStyles) {
        this._prevBodyStyles = {
          position: body.style.position,
          top: body.style.top,
          left: body.style.left,
          right: body.style.right,
          width: body.style.width,
          paddingRight: body.style.paddingRight,
          overflow: body.style.overflow
        };
      }

      // اندازه اسکرول‌بار برای جلوگیری از پرش layout
      const hasScrollbar = window.innerWidth > docEl.clientWidth;
      const scrollbarWidth = hasScrollbar ? (window.innerWidth - docEl.clientWidth) : 0;

      // فیکس کردن بدنه در موقعیت فعلی
      this._lockY = window.scrollY || window.pageYOffset || 0;
      body.style.position = 'fixed';
      body.style.top = `-${this._lockY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden'; // احتیاط
      if (scrollbarWidth) body.style.paddingRight = `${scrollbarWidth}px`;
    },

    unlockScroll() {
      const body = document.body;
      const y = this._lockY || 0;

      if (this._prevBodyStyles) {
        body.style.position = this._prevBodyStyles.position || '';
        body.style.top = this._prevBodyStyles.top || '';
        body.style.left = this._prevBodyStyles.left || '';
        body.style.right = this._prevBodyStyles.right || '';
        body.style.width = this._prevBodyStyles.width || '';
        body.style.paddingRight = this._prevBodyStyles.paddingRight || '';
        body.style.overflow = this._prevBodyStyles.overflow || '';
      } else {
        // fallback
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.paddingRight = '';
        body.style.overflow = '';
      }

      // برگشت به همان موقعیت اسکرول
      window.scrollTo(0, y);
      this._prevBodyStyles = null;
      this._lockY = 0;
    },
    toggleCategory(category) {
      category.expanded = !category.expanded;
    },
    isCategorySelected(category) {
      return this.selectedCategories.some(c => c.id === category.id);
    },
    toggleCategorySelect(category) {
      if (this.isCategorySelected(category)) {
        this.selectedCategories = this.selectedCategories.filter(c => c.id !== category.id);
      } else {
        this.selectedCategories = [...this.selectedCategories, category];
      }
    },
    removeCategory(category) {
      this.selectedCategories = this.selectedCategories.filter(c => c.id !== category.id);
      this.$emit('input', this.selectedCategories);
    },
    getCategoryName(category) {
      const names = [];
      let current = this.findCategoryInTree(category.id);
      while (current) {
        names.unshift(current.name);
        current = current.parent;
      }
      return names.join(' > ');
    },
    findCategoryInTree(id, categories = this.processedCategories) {
      for (const cat of categories) {
        if (cat.id === id) return cat;
        const found = (cat.children || []).length ? this.findCategoryInTree(id, cat.children) : null;
        if (found) return found;
      }
      return null;
    }
  },
  computed: {
    filteredCategories() {
      const q = (this.searchQuery || '').trim();
      if (!q) return this.processedCategories;
      return this.pruneTree(this.processedCategories, q);
    },
    flatCategoryOptions() {
      const out = [];
      const walk = (nodes, depth = 0) => {
        nodes.forEach(node => {
          out.push({
            id: node.id,
            label: node.name,
            depth: depth,
            ref: node,
          });
          if (node.children && node.children.length) {
            walk(node.children, depth + 1);
          }
        });
      };
      walk(this.processedCategories, 0);
      return out;
    },
    parentSelectedLabel() {
      if (!this.createForm.parent) return '';
      const found = this.flatCategoryOptions.find(c => c.id === this.createForm.parent);
      return found ? found.label : '';
    },

    filteredParentOptions() {
      const q = (this.parentSearch || '').trim().toLowerCase();
      if (!q) {
        return this.flatCategoryOptions;
      }
      return this.flatCategoryOptions.filter(opt =>
          String(opt.label || '').toLowerCase().includes(q)
      );
    },
  },
  beforeDestroy() {
    if (this.dialogOpen) this.unlockScroll();
  },
  watch: {
    categories(newVal) {
      this.processedCategories = this.processCategories(newVal);
    },
    value(newVal) {
      this.selectedCategories = [...(newVal || [])];
    },
    searchQuery(q) {
      this.applySearch(q);
    }
  }
}
</script>

<style scoped>
.category-field-wrapper { position: relative; }

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.dropdown-panel {
  margin-top: 20vh;
  width: min(90vw, 500px);
  max-height: min(80vh, 600px);
  box-shadow: 0 25px 50px -12px rgba(0,0,0,.25);
  border-radius: 12px;
  overflow: hidden;
  z-index: 9999;
  background: white;
}


.fade-scale-enter-active, .fade-scale-leave-active { transition: opacity 160ms ease, transform 160ms ease; }
.fade-scale-enter, .fade-scale-leave-to { opacity: 0; transform: scale(.98); }

.tree-container {
  max-height: calc(min(80dvh, 600px) - 200px);
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f3f4f6;
}
.tree-container::-webkit-scrollbar { width: 8px; }
.tree-container::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
.tree-container::-webkit-scrollbar-thumb { background-color: #cbd5e0; border-radius: 4px; }
.modal-overlay { z-index: 100000; }
.dropdown-panel {
  font-family: "iranyekan", "Vazirmatn", sans-serif !important;
  z-index: 100001;
}
label { font-weight: 600; color: #374151; margin-bottom: 0.5rem; display: block; }
</style>
